import React, { type ReactNode, type RefObject } from 'react';
import BoardIcon from '@atlaskit/icon/core/board';
import { fg } from '@atlassian/jira-feature-gating';
import LinkMenuItem from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { getTestId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { ENTITY_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import type { JiraLinkConfig } from '@atlassian/jira-router-components/src/ui/router-link-config/index.tsx';
import { RedirectOnLoad } from '@atlassian/redirect-on-load';
import type { RouterActionsType } from '@atlassian/react-resource-router';

export interface BoardLinkMenuItemProps {
	boardId: string;
	name: string;
	href: { to: string; prefetch: 'hover' };
	isSelected: boolean;
	actionsOnHover?: ReactNode;
	onClick?: () => void;
	shouldAutoSelect?: boolean;
	routerPush?: RouterActionsType['push'];
	dragAndDrop?: {
		draggableAnchorRef: RefObject<HTMLAnchorElement>;
		visualContentRef: RefObject<HTMLDivElement>;
		dropIndicator: ReactNode;
		isDragging: boolean;
		hasDragIndicator: boolean;
	};
}

export function BoardLinkMenuItem({
	boardId,
	name,
	href,
	isSelected,
	actionsOnHover,
	onClick,
	shouldAutoSelect = false,
	routerPush,
	dragAndDrop,
}: BoardLinkMenuItemProps) {
	const testId = getTestId(ENTITY_ID.BOARD(boardId));
	const icon = <BoardIcon label="" color="currentColor" />;

	// Common props for both variants of LinkMenuItem
	const commonProps = {
		elemBefore: icon,
		href,
		testId,
		actionsOnHover,
		onClick,
		isSelected,
		children: name,
	} as const;

	const dragAndDropProps = dragAndDrop
		? {
				ref: dragAndDrop.draggableAnchorRef,
				visualContentRef: dragAndDrop.visualContentRef,
				dropIndicator: dragAndDrop.dropIndicator,
				isDragging: dragAndDrop.isDragging,
				hasDragIndicator: dragAndDrop.hasDragIndicator,
			}
		: null;

	// Auto-select behavior with drag and drop support
	if (fg('navx-1724-add-auto-select-behaviour')) {
		const linkMenuItemWithDragAndDrop = (
			<LinkMenuItem<JiraLinkConfig> {...commonProps} {...dragAndDropProps} />
		);

		return shouldAutoSelect && routerPush ? (
			<RedirectOnLoad condition={shouldAutoSelect} href={href.to} redirect={routerPush}>
				{linkMenuItemWithDragAndDrop}
			</RedirectOnLoad>
		) : (
			linkMenuItemWithDragAndDrop
		);
	}

	// Standard behavior without auto-select
	return <LinkMenuItem<JiraLinkConfig> {...commonProps} {...dragAndDropProps} />;
}
